import type {EditablePaymentUpdateTerms, PaymentUpdateTerms} from '../dtos/payment-plan-update';

export function editablePaymentTerms(terms: PaymentUpdateTerms): EditablePaymentUpdateTerms {
	const {cashPrice, downPayment, tenorYears, frequency, leadingMonths, propertyUsage} = terms;
	return {cashPrice, downPayment, tenorYears, frequency, leadingMonths: frequency === 'Special' ? leadingMonths : 0,
		...(propertyUsage === 'Residential' || propertyUsage === 'Commercial' ? {propertyUsage} : {})};
}

export function samePaymentTerms(left: PaymentUpdateTerms, right: PaymentUpdateTerms): boolean {
	return JSON.stringify(editablePaymentTerms(left)) === JSON.stringify(editablePaymentTerms(right));
}

export function paymentTermsError(terms: PaymentUpdateTerms): string | null {
	if (!Number.isFinite(terms.cashPrice) || terms.cashPrice <= 0) return 'Enter a cash price greater than zero.';
	if (!Number.isFinite(terms.downPayment) || terms.downPayment < 0 || terms.downPayment >= terms.cashPrice) return 'Enter a down payment below the cash price and at least zero.';
	if (!Number.isFinite(terms.tenorYears) || terms.tenorYears <= 0) return 'Enter a tenor greater than zero.';
	if (terms.frequency === 'Special' && (!Number.isInteger(terms.leadingMonths) || terms.leadingMonths < 1)) return 'Enter a positive whole number for the Special installment count.';
	return null;
}

export function isStalePaymentUpdate(error: unknown): boolean {
	const candidate = error as {status?: number; response?: {status?: number}};
	return candidate?.status === 409 || candidate?.response?.status === 409;
}

export function paymentPlanUpdateError(error: unknown): string {
	const response = (error as {response?: {status?: number; data?: {message?: string | string[]}}})?.response;
	if (isStalePaymentUpdate(error)) return 'This payment plan changed while you were editing. Reload the latest plan before saving or submitting.';
	const readable = (value: unknown): string => {
		if (Array.isArray(value)) return value.map(readable).filter(Boolean).join('. ');
		if (value && typeof value === 'object') return readable((value as {en?: unknown; ar?: unknown}).en ?? (value as {ar?: unknown}).ar);
		if (typeof value !== 'string') return '';
		if ((value.startsWith('[') || value.startsWith('{'))) {try {return readable(JSON.parse(value));} catch { /* Ordinary text. */ }}
		return value;
	};
	return readable(response?.data?.message ?? (error as Error)?.message) || 'Could not update the payment plan. Please try again.';
}

/** Preserve authored labels; older servers send registry keys for these documents/messages. */
export function paymentUpdateLabel(value: string): string {
	const known: Record<string, string> = {
		'eoi-signed': 'Signed EOI', 'eoi-document': 'EOI document', 'contract-ibtdai': 'Preliminary contract',
		'workflow_payment_plan_decreased': 'Payment plan decreased: insurance follow-up',
		'workflow_payment_plan_medical_required': 'Medical assessment required for the updated payment plan',
	};
	if (known[value]) return known[value];
	if (!/[_-]/.test(value)) return value;
	const words = value.replace(/^workflow_/, '').replace(/[_-]+/g, ' ');
	return words.charAt(0).toUpperCase() + words.slice(1);
}
